import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  Fade,
  FormControlLabel,
  Grid,
  Modal,
  Paper,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';

import { IconAndCount } from 'pages/Home/FleetsFoundUI';
import { useTranslation } from 'react-i18next';
import { BsTruckFlatbed } from 'react-icons/bs';
import { VehicleOverviewCount } from './VehicleOverviewCount';
import { IoFlagOutline } from 'react-icons/io5';

import { ReactComponent as Merged } from 'assets/svg/home/merged_home.svg';
import { ReactComponent as Truck } from 'assets/svg/home/truck_home.svg';
import { ReactComponent as Trailer } from 'assets/svg/home/trailer.svg';
import { ReactComponent as Configured } from 'assets/svg/home/config.svg';
import { ReactComponent as Unconfigured } from 'assets/svg/home/not_config.svg';
import { ReactComponent as PartlyConfigured } from 'assets/svg/home/part_config.svg';
import PropTypes from 'prop-types';
import * as useClasses from 'pages/Home/const';
import { useSelector } from 'react-redux';
import { FaBolt, FaRegClock, FaParking } from 'react-icons/fa';
import { ReactComponent as EBus } from 'assets/svg/common/e_BusIcon.svg';
import { ReactComponent as Bus } from 'assets/svg/common/busLogo.svg';
import { useDispatch } from 'react-redux';
import { setParameters } from 'redux/fleets/ducks';
import { getOverviewChart } from 'services/fleet_service';
import OverviewChart from '../TireOverview/OverviewChart';

const TRUCK = -1;
const TRAILER = -2;
const BUS = -3;
const E_TRUCK = -4;
const E_BUS = -5;
const FULL_CONFIG = 0;
const PART_CONFIG = 1;
const UNCONFIG = 2;
const MERGED = 4;
const OVERDUE = 3;
const NON_COMPLIANT = 5;
const PARKED = 6;

const metric_map = {
  truck: 'TRUCK_COUNT',
  trailer: 'TRAILER_COUNT',
  bus: 'BUS_COUNT',
  e_truck: 'E_TRUCK_COUNT',
  e_bus: 'E_BUS_COUNT',
  fullConfig: 'FULLY_CONFIG',
  partConfig: 'PARTIALLY_CONFIG',
  unConfig: 'UN_CONFIG',
  nonCompliantCount: 'NON_COMPLIANT',
};

export const VehicleOverview = ({ isLoading }) => {
  const { t } = useTranslation();
  const { classes } = useClasses.style();

  const [open, setOpen] = useState(false);
  const [chartTitle, setChartTitle] = useState(null);
  const [dataSets, setDataSets] = useState(null);
  const theme = useTheme();
  const [checked, setChecked] = useState(false);
  const [flag, setFlag] = useState(false);
  const handleChange = event => {
    setChecked(event.target.checked);
  };
  const [location_, setLocation] = useState(null);
  const [days, setDays] = useState(30);
  let vehiclesOverviewinfo = useSelector(state => state.fleet.vehicleOverview);
  const iconConfig = {
    fill: theme.palette.primary.main,
  };
  const chart_title = {
    truck: t('common.truck'),
    trailer: t('common.trailer'),
    bus: t('common.bus'),
    e_truck: t('common.eTruck'),
    e_bus: t('common.eTruck'),
    fullConfig: t('common.configured'),
    partConfig: t('common.partialConfig'),
    unConfig: t('common.notConfigured'),
    nonCompliantCount: t('vehiclePage.compliance.NON_COMPLIANT'),
  };
  const navigate = useNavigate();
  const dispatch = useDispatch();
  useEffect(() => {
    if (metric_map[location_] && parseInt(days)) {
      fetchOverviewChart(location_);
    }
    // eslint-disable-next-line
  }, [days]);
  const fetchOverviewChart = async category => {
    let params = {
      section: 'vehicle_overview',
      metric_name: metric_map[category],
      days: parseInt(days),
    };
    try {
      const res = await getOverviewChart(params);
      if (res?.status === 200) {
        let resources = res?.data?.resources;

        let xAxis = resources?.x;
        let yAxis = resources?.y;
        let temp = [];
        for (let i = 0; i < xAxis.length; i++) {
          let data = 0;
          if (yAxis[i]) {
            data = yAxis[i];
          }
          temp.push({
            name: xAxis[i],
            count: data,
          });
        }
        setDataSets(temp);
      }
    } catch (err) {
      console.error(err);
    }
  };
  const boxClickHandler = async (identifier, category) => {
    setLocation(category);
    if (identifier && identifier >= 0 && !checked) {
      dispatch(
        setParameters({
          vehicleTabValue: identifier,
          vehicleLocation: category,
          accessing: 'ONLY_VEHICLE',
        })
      );
      navigate('/overview/vehicle');
    } else if (checked) {
      console.log(metric_map[category]);
      if (metric_map[category]) {
        fetchOverviewChart(category);
      } else {
        setFlag(true);
      }
      setChartTitle(chart_title[category]);
      setOpen(true);
    }
  };
  const handleClose = () => {
    setDays(30);
    setLocation(null);
    setFlag(false);
    setDataSets(null);
    setOpen(false);
  };
  const renderLoader = () => {
    return (
      <Box
        display='flex'
        flexDirection={'column'}
        justifyContent={'center'}
        alignItems={'center'}
        width='100%'
        height='100%'
      >
        <IconAndCount
          icon={<BsTruckFlatbed size={60} {...iconConfig} />}
          isLoading={isLoading}
        />
      </Box>
    );
  };

  const renderNoVehicleState = () => {
    return (
      <Box display='flex' flexDirection={'column'} justifyContent={'center'}>
        <IconAndCount
          icon={<BsTruckFlatbed size={60} {...iconConfig} />}
          count={0}
        />
        <Typography
          variant='h6'
          component='span'
          className={classes.manageTiresText}
        >
          {t('common.noVehiclesAdded')}{' '}
          <Link to='/fleets-data' className={classes.tireInventoryLink}>
            Fleets page
          </Link>
        </Typography>
      </Box>
    );
  };

  const renderVehicleOverview = () => {
    if (isLoading) {
      return renderLoader();
    }

    if (!vehiclesOverviewinfo) {
      return renderNoVehicleState();
    }
    const {
      bus,
      trailer,
      truck,
      e_truck,
      e_bus,
      overdueFleetcheckCount,
      parkedCount,
      nonCompliantCount,
    } = vehiclesOverviewinfo;

    const hasAnyCountGreaterThanZero =
      bus?.count > 0 ||
      trailer?.count > 0 ||
      truck?.count > 0 ||
      e_truck?.count > 0 ||
      e_bus?.count > 0 ||
      overdueFleetcheckCount > 0 ||
      parkedCount > 0 ||
      nonCompliantCount > 0;

    const configurations = {
      partconfig:
        bus.partConfig +
        trailer.partConfig +
        truck.partConfig +
        e_truck.partConfig +
        e_bus.partConfig,
      fullConfig:
        bus.fullConfig +
        trailer.fullConfig +
        truck.fullConfig +
        e_truck.fullConfig +
        e_bus.fullConfig,
      unConfig:
        bus.unConfig +
        trailer.unConfig +
        truck.unConfig +
        e_truck.unConfig +
        e_bus.unConfig,
    };

    const isVehicleAvailable = entityNumber => {
      return entityNumber > 0;
    };

    return (
      <>
        {hasAnyCountGreaterThanZero ? (
          <Grid container spacing={2}>
            {isVehicleAvailable(truck.count) && (
              <VehicleOverviewCount
                boxClickHandler={() => boxClickHandler(TRUCK, 'truck')}
                src={<Truck stroke={theme.palette.primary.main} />}
                showIcon={true}
                count={truck.count}
                name={t('common.truck')}
              />
            )}
            {isVehicleAvailable(e_truck.count) && (
              <VehicleOverviewCount
                src={
                  <Box position={'relative'}>
                    <Truck stroke={theme.palette.primary.main} />
                    <FaBolt
                      color={theme.palette.primary.main}
                      style={{ position: 'absolute', left: 50, top: 0 }}
                    />
                  </Box>
                }
                boxClickHandler={() => boxClickHandler(E_TRUCK, 'e_truck')}
                showIcon={true}
                count={e_truck.count}
                name={t('common.eTruck')}
              />
            )}
            {isVehicleAvailable(bus.count) && (
              <VehicleOverviewCount
                boxClickHandler={() => boxClickHandler(BUS, 'bus')}
                src={<Bus width={45} fill={theme.palette.primary.main} />}
                showIcon={true}
                count={bus.count}
                name={t('common.bus')}
              />
            )}
            {isVehicleAvailable(e_bus.count) && (
              <VehicleOverviewCount
                boxClickHandler={() => boxClickHandler(E_BUS, 'e_bus')}
                src={<EBus width={45} fill={theme.palette.primary.main} />}
                showIcon={true}
                count={e_bus.count}
                name={t('common.eBus')}
              />
            )}
            {isVehicleAvailable(truck.merged) && (
              <VehicleOverviewCount
                src={<Merged fill={theme.palette.primary.main} />}
                showIcon={true}
                count={truck.merged}
                name={t('homePage.merged')}
                identifier={MERGED}
                boxClickHandler={() => boxClickHandler(MERGED, 'merged')}
                category={'merged'}
              />
            )}
            {isVehicleAvailable(trailer.count) && (
              <VehicleOverviewCount
                src={<Trailer fill={theme.palette.primary.main} />}
                boxClickHandler={() => boxClickHandler(TRAILER, 'trailer')}
                showIcon={true}
                count={trailer.count}
                name={t('common.trailer')}
              />
            )}
            {isVehicleAvailable(configurations.fullConfig) && (
              <VehicleOverviewCount
                src={<Configured fill={theme.palette.primary.main} />}
                showIcon={true}
                count={configurations.fullConfig}
                name={t('common.configured')}
                identifier={FULL_CONFIG}
                boxClickHandler={() =>
                  boxClickHandler(FULL_CONFIG, 'fullConfig')
                }
                category={'fullConfig'}
              />
            )}
            {isVehicleAvailable(configurations.partconfig) && (
              <VehicleOverviewCount
                src={<PartlyConfigured fill={theme.palette.primary.main} />}
                showIcon={true}
                count={configurations.partconfig}
                name={t('common.partialConfig')}
                identifier={PART_CONFIG}
                boxClickHandler={() =>
                  boxClickHandler(PART_CONFIG, 'partConfig')
                }
                category={'partConfig'}
              />
            )}
            {isVehicleAvailable(configurations.unConfig) && (
              <VehicleOverviewCount
                src={<Unconfigured fill={theme.palette.primary.main} />}
                showIcon={true}
                count={configurations.unConfig}
                name={t('homePage.unconfigured')}
                identifier={UNCONFIG}
                boxClickHandler={() => boxClickHandler(UNCONFIG, 'unConfig')}
                category={'unConfig'}
              />
            )}

            <VehicleOverviewCount
              src={<FaRegClock size={35} fill={theme.palette.primary.main} />}
              showIcon={true}
              count={overdueFleetcheckCount}
              name={t('homePage.overdue')}
              identifier={OVERDUE}
              boxClickHandler={() =>
                boxClickHandler(OVERDUE, 'overdueFleetcheckCount')
              }
              category={'overdueFleetcheck'}
            />
            <VehicleOverviewCount
              src={
                <IoFlagOutline
                  size={35}
                  stroke={theme.palette.secondary.main}
                />
              }
              showIcon={true}
              count={nonCompliantCount}
              name={t('homePage.nonCompliant')}
              identifier={NON_COMPLIANT}
              boxClickHandler={() =>
                boxClickHandler(NON_COMPLIANT, 'nonCompliantCount')
              }
              category={'nonCompliant'}
            />
            <VehicleOverviewCount
              src={<FaParking size={35} fill={theme.palette.primary.main} />}
              showIcon={true}
              count={parkedCount}
              name={t('homePage.parked')}
              identifier={PARKED}
              boxClickHandler={() => boxClickHandler(PARKED, 'parkedCount')}
              category={'parked'}
            />
          </Grid>
        ) : (
          <Box
            display={'flex'}
            height={'100%'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <Typography
              variant='h5'
              component='span'
              className={classes.manageTiresText}
            >
              {t('fleetOverviewPage.noVehicleMessage')}
            </Typography>
          </Box>
        )}
      </>
    );
  };

  return (
    <Paper
      style={{
        height: '100%',
        padding: '16px 20px',
        boxShadow: '0px 2px 16px 0px rgba(179, 179, 179, 0.10)',
      }}
    >
      <Box
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
        }}
      >
        <Typography variant='subtitle1' gutterBottom fontWeight={900}>
          {t('common.vehicleOverview')}
        </Typography>
        <FormControlLabel
          value='top'
          control={
            <Switch color='primary' checked={checked} onChange={handleChange} />
          }
          label={t('common.graphical_mode')}
          labelPlacement='end'
        />
      </Box>

      <Box>{renderVehicleOverview()}</Box>
      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          border: 'none',
        }}
      >
        <Fade in={open}>
          <Card sx={{ minWidth: 600, border: 'none' }}>
            <CardContent>
              {!flag && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant='h5'
                      gutterBottom
                      fontWeight={900}
                      align={'center'}
                    >
                      {chartTitle}
                    </Typography>
                  </Box>
                  <Box sx={{ float: 'right' }}>
                    <TextField
                      id='outlined-basic'
                      sx={{ maxWidth: 100 }}
                      label='Days'
                      type='number'
                      variant='outlined'
                      value={days}
                      InputProps={{
                        inputProps: {
                          min: 1,
                        },
                      }}
                      onChange={e => {
                        let val = e.target.value;
                        setTimeout(() => setDays(val), 300);
                      }}
                    />
                  </Box>
                </Box>
              )}

              {dataSets?.length && <OverviewChart dataSets={dataSets} />}
              {flag && (
                <Alert severity='error'>
                  <AlertTitle>{t('common.data_not_present')}</AlertTitle>
                  {t('common.no_graphical_data')}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Fade>
      </Modal>
    </Paper>
  );
};

VehicleOverview.propTypes = {
  isLoading: PropTypes.bool,
};
